/**
 * Shared TanStack Query hooks over the typed client. Centralizing the query keys
 * here keeps cache invalidation consistent across components.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { facetValueClause } from "@/lib/fields";

import {
  applyBulkEditFile,
  applyUikBranchFile,
  applyReadiness,
  bulkDeleteVolunteers,
  bulkUpdateVolunteers,
  cancelJob,
  countVolunteers,
  getAdminAudit,
  getAdminJobs,
  getAdminLotterySettings,
  getAdminSystem,
  getAdminUsers,
  getLotteryStatus,
  createTouch,
  dataUpdaterApply,
  dataUpdaterPreview,
  deleteTouch,
  fillTouch,
  getCells,
  getCellsSize,
  getCellTypes,
  getPrimaries,
  getRefs,
  getSavedFilters,
  getTouchComposition,
  getUiks,
  getVolunteer,
  getVolunteerHistory,
  listJobs,
  listTouches,
  listVolunteers,
  putAdminLotterySettings,
  removeFromTouch,
  sampleVolunteers,
  searchCells,
  submitLotteryTurnout,
  testAdminLotterySettings,
  whoami,
  type AdminAudit,
  type AdminAuditParams,
  type AdminJob,
  type AdminJobsParams,
  type AdminSystem,
  type AdminUser,
  type Cell,
  type CellSearchResult,
  type CellsSize,
  type CellType,
  type CountResponse,
  type CreateTouchForm,
  type DataUpdaterApplyRequest,
  type DataUpdaterApplyResult,
  type DataUpdaterPreview,
  type FillTouchBody,
  type FilterClause,
  type GetCellsParams,
  type JobInfo,
  type JobListItem,
  type ListParams,
  type ListTouchesParams,
  type LotterySettingsBody,
  type LotterySettingsInfo,
  type LotteryStatus,
  type LotteryTestResult,
  type LotteryTurnoutBody,
  type PrimaryOrg,
  type Refs,
  type SampleResponse,
  type SavedFilter,
  type SessionInfo,
  type Touch,
  type UikBranchFileRow,
  type TouchCompositionKind,
  type TouchCompositionPage,
  type TouchCompositionParams,
  type TouchesPage,
  type Uik,
  type Volunteer,
  type VolunteerHistoryPage,
  type VolunteersPage,
  type BulkSelection,
  type BulkUpdateValues,
} from "@/api/client";

export const queryKeys = {
  session: ["session"] as const,
  refs: ["refs"] as const,
  savedFilters: ["saved-filters"] as const,
  volunteers: (params: ListParams) => ["volunteers", params] as const,
  count: (filters: FilterClause[]) => ["volunteers-count", filters] as const,
  touches: (params: ListTouchesParams) => ["touches", params] as const,
  touchComposition: (
    touchId: number,
    kind: TouchCompositionKind,
    params: TouchCompositionParams,
  ) => ["touch-composition", touchId, kind, params] as const,
  cells: (parentId?: number, title?: string) =>
    ["cells", parentId ?? null, title ?? null] as const,
  cellSearch: (q: string) => ["cell-search", q] as const,
  cellsSize: (id: number) => ["cells-size", id] as const,
  primaries: (branchId: number) => ["primaries", branchId] as const,
  uiks: (branchId: number) => ["uiks", branchId] as const,
  cellTypes: ["cell-types"] as const,
  job: (id: number) => ["job", id] as const,
  jobs: (params: { status?: string; kind?: string }) => ["jobs", params] as const,
  volunteer: (id: number) => ["volunteer", id] as const,
  volunteerHistory: (id: number) => ["volunteer-history", id] as const,
  adminUsers: ["admin", "users"] as const,
  adminJobs: (params: AdminJobsParams) => ["admin", "jobs", params] as const,
  adminAudit: (params: AdminAuditParams) => ["admin", "audit", params] as const,
  adminSystem: ["admin", "system"] as const,
  lotteryStatus: ["lottery", "status"] as const,
  adminLotterySettings: ["admin", "lottery", "settings"] as const,
};

export function useSession(): UseQueryResult<SessionInfo, unknown> {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: whoami,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useRefs(enabled = true): UseQueryResult<Refs, unknown> {
  return useQuery({
    queryKey: queryKeys.refs,
    queryFn: getRefs,
    enabled,
    staleTime: 60 * 60_000, // refs change rarely; backend also caches
  });
}

export function useVolunteers(
  params: ListParams,
  enabled = true,
): UseQueryResult<VolunteersPage, unknown> {
  return useQuery({
    queryKey: queryKeys.volunteers(params),
    queryFn: () => listVolunteers(params),
    enabled,
    placeholderData: (prev) => prev, // keep prior page visible while fetching next
  });
}

/**
 * Single activist for the card screen. There is no by-id endpoint upstream
 * (MOB_API_REVERSE p.55), so getVolunteer reads one row from the listing via an
 * `id among [id]` filter.
 */
export function useVolunteer(
  id: number,
  enabled = true,
): UseQueryResult<Volunteer | undefined, unknown> {
  return useQuery({
    queryKey: queryKeys.volunteer(id),
    queryFn: () => getVolunteer(id),
    enabled: enabled && Number.isFinite(id),
  });
}

/** Touch history sub-resource for the activist card timeline. */
export function useVolunteerHistory(
  id: number,
  enabled = true,
): UseQueryResult<VolunteerHistoryPage, unknown> {
  return useQuery({
    queryKey: queryKeys.volunteerHistory(id),
    queryFn: () => getVolunteerHistory(id),
    enabled: enabled && Number.isFinite(id),
  });
}

export function useCount(
  filters: FilterClause[],
  enabled = true,
): UseQueryResult<CountResponse, unknown> {
  return useQuery({
    queryKey: queryKeys.count(filters),
    queryFn: () => countVolunteers(filters),
    enabled,
    placeholderData: (prev) => prev,
  });
}

/**
 * Facet count: how many volunteers match `otherFilters` (applied clauses of the
 * OTHER facet groups) AND `field == value`. Used by FacetsPanel to label each
 * checkbox. Thin wrapper over useCount with the standard query cache (refs change
 * rarely, so counts can be reused across renders).
 */
export function useFacetCount(
  otherFilters: FilterClause[],
  field: string,
  value: unknown,
  enabled = true,
): UseQueryResult<CountResponse, unknown> {
  // Operator per field kind: multiselect fields need `among` even for one value (F22).
  const combined: FilterClause[] = [...otherFilters, facetValueClause(field, value)];
  return useQuery({
    queryKey: queryKeys.count(combined),
    queryFn: () => countVolunteers(combined),
    enabled,
    placeholderData: (prev) => prev,
    staleTime: 5 * 60_000,
  });
}

/**
 * Mutation over sampleVolunteers: pick up to `n` random ids matching `filters`.
 * Caller feeds the resulting ids into selection.selectRandomN(n, {mode:'filter', ids}).
 */
export function useSampleVolunteers(): UseMutationResult<
  SampleResponse,
  unknown,
  { filters: FilterClause[]; n: number }
> {
  return useMutation({
    mutationFn: ({ filters, n }: { filters: FilterClause[]; n: number }) =>
      sampleVolunteers(filters, n),
  });
}

export function useSavedFilters(enabled = true): UseQueryResult<SavedFilter[], unknown> {
  return useQuery({
    queryKey: queryKeys.savedFilters,
    queryFn: getSavedFilters,
    enabled,
  });
}

// --- Touches (касания) ---

export function useTouches(
  params: ListTouchesParams = {},
  enabled = true,
): UseQueryResult<TouchesPage, unknown> {
  return useQuery({
    queryKey: queryKeys.touches(params),
    queryFn: () => listTouches(params),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useTouchComposition(
  touchId: number,
  kind: TouchCompositionKind,
  params: TouchCompositionParams = {},
  enabled = true,
): UseQueryResult<TouchCompositionPage, unknown> {
  return useQuery({
    queryKey: queryKeys.touchComposition(touchId, kind, params),
    queryFn: () => getTouchComposition(touchId, kind, params),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useCells(
  params: GetCellsParams = {},
  enabled = true,
): UseQueryResult<Cell[], unknown> {
  return useQuery({
    queryKey: queryKeys.cells(params.parentId, params.title),
    queryFn: () => getCells(params),
    enabled,
    staleTime: 60 * 60_000,
  });
}

/**
 * Flat cell-title search against OUR backend (GET /api/cells/search). Powers the
 * "Найти ячейку" box in <CellTreeSelect>. Disabled for blank queries so an empty
 * box never hits the network.
 */
export function useCellSearch(
  q: string,
  enabled: boolean,
): UseQueryResult<CellSearchResult, unknown> {
  return useQuery({
    queryKey: queryKeys.cellSearch(q),
    queryFn: () => searchCells(q),
    enabled: enabled && q.trim().length > 0,
    staleTime: 60 * 60_000,
  });
}

export function useCellsSize(
  cellId: number | null,
  enabled = true,
): UseQueryResult<CellsSize, unknown> {
  return useQuery({
    queryKey: queryKeys.cellsSize(cellId ?? -1),
    queryFn: () => getCellsSize(cellId as number),
    enabled: enabled && cellId != null,
    staleTime: 5 * 60_000,
  });
}

export function usePrimaries(
  branchId: number | null,
  enabled = true,
): UseQueryResult<PrimaryOrg[], unknown> {
  return useQuery({
    queryKey: queryKeys.primaries(branchId ?? -1),
    queryFn: () => getPrimaries(branchId as number),
    enabled: enabled && branchId != null,
    staleTime: 30 * 60_000,
  });
}

export function useUiks(
  branchId: number | null,
  enabled = true,
): UseQueryResult<Uik[], unknown> {
  return useQuery({
    queryKey: queryKeys.uiks(branchId ?? -1),
    queryFn: () => getUiks(branchId as number),
    enabled: enabled && branchId != null,
    staleTime: 30 * 60_000,
  });
}

export function useCellTypes(enabled = true): UseQueryResult<CellType[], unknown> {
  return useQuery({
    queryKey: queryKeys.cellTypes,
    queryFn: getCellTypes,
    enabled,
    staleTime: 60 * 60_000,
  });
}

export function useCreateTouch(): UseMutationResult<Touch, unknown, CreateTouchForm> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (form: CreateTouchForm) => createTouch(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["touches"] });
    },
  });
}

export function useDeleteTouch(): UseMutationResult<void, unknown, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteTouch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["touches"] });
    },
  });
}

export function useRemoveFromTouch(): UseMutationResult<void, unknown, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resultId: number) => removeFromTouch(resultId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["touch-composition"] });
      queryClient.invalidateQueries({ queryKey: ["touches"] });
    },
  });
}

export function useFillTouch(): UseMutationResult<
  { job_id: number },
  unknown,
  { touchId: number; body: FillTouchBody }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ touchId, body }: { touchId: number; body: FillTouchBody }) =>
      fillTouch(touchId, body),
    onSuccess: () => {
      // A fill creates a job (immediate or scheduled) — make it show up in the
      // «Задачи» list and the header indicator without a manual reload (F19).
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

// --- Jobs / Scheduler (Wave 5, feature #8) ---

/** Statuses that mean the list may still change (so we keep polling). */
const LIVE_JOB_STATUSES = new Set<JobInfo["status"]>(["scheduled", "pending", "running"]);

/**
 * List the current user's jobs (optionally filtered). Polls every 5s while any row
 * is still live (scheduled/pending/running) so a fired/finished scheduled job updates;
 * stops polling once everything is terminal.
 */
export function useJobs(
  params: { status?: JobInfo["status"]; kind?: string } = {},
  enabled = true,
): UseQueryResult<JobListItem[], unknown> {
  return useQuery({
    queryKey: queryKeys.jobs(params),
    queryFn: () => listJobs(params),
    enabled,
    refetchInterval: (q) => {
      const rows = q.state.data;
      // Live rows → fast poll. Empty/all-terminal → a slow heartbeat instead of
      // stopping forever, so a job that appears (scheduler firing, another tab, a
      // just-created job) is still picked up while the page is open (F19).
      if (rows && rows.some((r) => LIVE_JOB_STATUSES.has(r.status))) return 5000;
      return 20000;
    },
    refetchIntervalInBackground: false,
  });
}

/**
 * Active (running) jobs for the global header indicator. Unlike useJobs, this polls on a
 * FIXED interval (default 4s) regardless of the current list contents, so a job that
 * starts while the user is on another screen is discovered even when the list is momentarily
 * empty. Scoped to the current user by the backend (er_login).
 */
export function useActiveJobs(
  intervalMs = 4000,
  enabled = true,
): UseQueryResult<JobListItem[], unknown> {
  return useQuery({
    queryKey: queryKeys.jobs({ status: "running" }),
    queryFn: () => listJobs({ status: "running" }),
    enabled,
    refetchInterval: intervalMs,
    refetchIntervalInBackground: false,
  });
}

/** Cancel a job (scheduled or running). Invalidates the jobs list + the single job. */
export function useCancelJob(): UseMutationResult<void, unknown, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cancelJob(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.job(id) });
    },
  });
}

// --- Bulk operations over the current selection (Wave 4a) ---

/**
 * Bulk field update over the current selection (one server request). On success the
 * underlying rows changed, so invalidate the volunteers list + count queries.
 */
export function useBulkUpdateVolunteers(): UseMutationResult<
  { count: number },
  unknown,
  { selection: BulkSelection; values: BulkUpdateValues }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ selection, values }: { selection: BulkSelection; values: BulkUpdateValues }) =>
      bulkUpdateVolunteers(selection, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["volunteers"] });
      queryClient.invalidateQueries({ queryKey: ["volunteers-count"] });
    },
  });
}

/**
 * Bulk delete over the current selection. Returns a job_id for <JobProgress/>; the
 * list/count invalidation happens via the JobProgress onDone callback once the job
 * finishes, so it is NOT done here.
 */
export function useBulkDeleteVolunteers(): UseMutationResult<
  { job_id: number },
  unknown,
  { selection: BulkSelection }
> {
  return useMutation({
    mutationFn: ({ selection }: { selection: BulkSelection }) =>
      bulkDeleteVolunteers(selection),
  });
}

// --- Readiness (массовая «Готовность регион» по списку id из Excel, Wave 4c) ---

/**
 * Bulk-set readiness_region for a list of volunteer ids. Returns a job_id for the
 * resumable backend job; the rows change upstream as it runs, so invalidate the jobs
 * list (для «Задачи»/индикатора в шапке) plus the volunteers list + count on success.
 */
export function useApplyReadiness(): UseMutationResult<
  { job_id: number },
  unknown,
  { ids: number[]; value: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids: number[]; value: string }) => applyReadiness(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      queryClient.invalidateQueries({ queryKey: ["volunteers"] });
      queryClient.invalidateQueries({ queryKey: ["volunteers-count"] });
    },
  });
}

// --- Обновление из файла (один признак всем по ключу id/телефон, Wave 4d) ---

/**
 * Bulk-set ONE editable field for every row keyed by id or phone from an uploaded file.
 * Returns a job_id for the resumable backend job; rows change upstream as it runs, so
 * invalidate the jobs list (для «Задачи»/индикатора в шапке) plus the volunteers list +
 * count on success — mirrors useApplyReadiness.
 */
export function useApplyBulkEditFile(): UseMutationResult<
  { job_id: number },
  unknown,
  { key_type: "id" | "phone"; keys: (number | string)[]; values: Record<string, unknown> }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      key_type: "id" | "phone";
      keys: (number | string)[];
      values: Record<string, unknown>;
    }) => applyBulkEditFile(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      queryClient.invalidateQueries({ queryKey: ["volunteers"] });
      queryClient.invalidateQueries({ queryKey: ["volunteers-count"] });
    },
  });
}

// --- УИК → отделение из файла (проставить uik_id + branch_id, Wave 4e) ---

/**
 * Проставить УИК (uik_id) + местное отделение (branch_id) для строк из файла по ключу
 * id/телефон. Клиент шлёт только `{key, uik_n}` — отделение вычисляет сервер из refs.uiks.
 * Возвращает job_id резюмируемой задачи; строки меняются на апстриме по мере выполнения,
 * поэтому инвалидируем список задач (для «Задачи»/индикатора в шапке) плюс список
 * активистов + счётчик — зеркалит useApplyBulkEditFile.
 */
export function useApplyUikBranchFile(): UseMutationResult<
  { job_id: number },
  unknown,
  { key_type: "id" | "phone"; rows: UikBranchFileRow[] }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { key_type: "id" | "phone"; rows: UikBranchFileRow[] }) =>
      applyUikBranchFile(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      queryClient.invalidateQueries({ queryKey: ["volunteers"] });
      queryClient.invalidateQueries({ queryKey: ["volunteers-count"] });
    },
  });
}

// --- Data Updater (массовое обновление по файлу Excel, Wave 4b) ---

/**
 * Phase 1 — preview an uploaded xlsx (read-only, no write). Returns
 * {stats, validColumns, items}. No cache invalidation (nothing changed upstream).
 */
export function useDataUpdaterPreview(): UseMutationResult<DataUpdaterPreview, unknown, File> {
  return useMutation({
    mutationFn: (file: File) => dataUpdaterPreview(file),
  });
}

/**
 * Phase 2 — apply the update to the live base. Rows changed upstream, so invalidate
 * the volunteers list + count queries on success.
 */
export function useDataUpdaterApply(): UseMutationResult<
  DataUpdaterApplyResult,
  unknown,
  DataUpdaterApplyRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DataUpdaterApplyRequest) => dataUpdaterApply(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["volunteers"] });
      queryClient.invalidateQueries({ queryKey: ["volunteers-count"] });
    },
  });
}

// --- Admin (суперадмин, только просмотр) ---

/** Per-user overview across all ER accounts. Light polling keeps counters fresh. */
export function useAdminUsers(enabled = true): UseQueryResult<AdminUser[], unknown> {
  return useQuery({
    queryKey: queryKeys.adminUsers,
    queryFn: getAdminUsers,
    enabled,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

/** Every user's jobs (no owns-filter), optionally filtered. Polls while mounted. */
export function useAdminJobs(
  params: AdminJobsParams = {},
  enabled = true,
): UseQueryResult<AdminJob[], unknown> {
  return useQuery({
    queryKey: queryKeys.adminJobs(params),
    queryFn: () => getAdminJobs(params),
    enabled,
    placeholderData: (prev) => prev,
    refetchInterval: 10_000,
  });
}

/** Action audit log, newest first, optionally filtered. */
export function useAdminAudit(
  params: AdminAuditParams = {},
  enabled = true,
): UseQueryResult<AdminAudit[], unknown> {
  return useQuery({
    queryKey: queryKeys.adminAudit(params),
    queryFn: () => getAdminAudit(params),
    enabled,
    placeholderData: (prev) => prev,
  });
}

/** Egress/throttle state + global job counters. Polls every 5s for a live view. */
export function useAdminSystem(enabled = true): UseQueryResult<AdminSystem, unknown> {
  return useQuery({
    queryKey: queryKeys.adminSystem,
    queryFn: getAdminSystem,
    enabled,
    refetchInterval: 5_000,
  });
}

/* ───────────── ЕДГ-лотерея ───────────── */

export function useLotteryStatus(enabled = true): UseQueryResult<LotteryStatus, unknown> {
  return useQuery({ queryKey: queryKeys.lotteryStatus, queryFn: getLotteryStatus, enabled, staleTime: 30_000 });
}

export function useSubmitLotteryTurnout(): UseMutationResult<{ job_id: number }, unknown, LotteryTurnoutBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LotteryTurnoutBody) => submitLotteryTurnout(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.lotteryStatus });
    },
  });
}

export function useAdminLotterySettings(enabled = true): UseQueryResult<LotterySettingsInfo, unknown> {
  return useQuery({ queryKey: queryKeys.adminLotterySettings, queryFn: getAdminLotterySettings, enabled });
}

export function useSaveLotterySettings(): UseMutationResult<LotterySettingsInfo, unknown, LotterySettingsBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LotterySettingsBody) => putAdminLotterySettings(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminLotterySettings });
      queryClient.invalidateQueries({ queryKey: queryKeys.lotteryStatus });
    },
  });
}

export function useTestLotterySettings(): UseMutationResult<LotteryTestResult, unknown, void> {
  return useMutation({ mutationFn: () => testAdminLotterySettings() });
}
